import { useReducer, useEffect, useContext, useMemo } from 'react'
import {
    Autocomplete,
    Button,
    Switch,
    FormControlLabel,
    TextField,
    Typography,
    FormControl,
    Box,
    Tooltip,
    Chip,
    debounce,
} from '@mui/material'
import {
    addBoat,
    addTag,
    getEngines,
    getManufacturers,
    unlinkTag,
    getBoatModels,
    getUsers,
    getDealers,
    getDevices,
    getUserQuery,
    getBoatModelQuery,
    getDealerQuery,
    getDeviceQuery,
    addGuestToRegistration,
} from '../../../../../apiRoutes'
import {
    basicValidator,
    dateValidator,
} from '../../../../../utilities/functionTools'

import {
    FormSection,
    SaveButton,
    UniversalTagToolbar,
    Autocomplete as UniversalAutocomplete,
    UniversalAccordion,
    Grid,
} from '../../../../UniversalComponents'
import responseError from '../../../../../context/responseError/responseError'
import { ToastMessage } from '../../../../../context/ToastPopUpContext'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useGlobalState } from '../../../../../context/GlobalStateContext'

const BoatConfigurationForm = ({
    row,
    formDescription,
    onClose,
    saveCache,
    permissionCheck,
    superAdmin,
}) => {
    const setToastMessage = useContext(ToastMessage)
    const defaultState = {
        formData: {
            shortHin: row.shortHin || '',
            fullHin: row.fullHin || '',
            nickname: row?.nickname || '',
            device: row.deviceSerial || '',
            ownerEmail: row.ownerEmail || '',
            productionStartDate: row?.productionStartDate
                ? dateValidator(row?.productionStartDate)
                : null,
            shipDate: row?.shipDate ? dateValidator(row?.shipDate) : null,
            deliveryDate: row?.deliveryDate
                ? dateValidator(row?.deliveryDate)
                : null,
            gpsEnabled: row.id ? row.gpsEnabled : true,
            dealerVisibility: row.id ? row.dealerVisibility : false,
            demoBoat: row.id ? row.demoBoat : false,
            bluetoothEnabled: row.id ? row.bluetoothEnabled : false,
            salesStatus: row.salesStatus || '',
            boatModel: row.model
                ? { label: `${row.modelYear} - ${row.model}` }
                : '',
            dealer: row.dealerName || '',
            id: row.id || '',
            engineModel: row.engineModel || '',
            engineManufacturer: row.engineManufacturer || '',
            updatedBy: row.updatedBy || '',
            manufacturer: row?.manufacturer?.manufacturerName,
            engineSerial: row.engineSerial || '',
            tags: row.tags || [],
            telemetryPreferences: row?.telemetryPreferences || {
                ownerGps: true,
                geoFencing: false,
                guestGps: false,
                dealerGps: false,
                manufacturerGps: false,
                ownerSignalNotifications: true,
                guestSignalNotifications: false,
                dealerSignalNotifications: true,
                manufacturerSignalNotifications: false,
            },
            owner: row?.owner || '',
            guests: row?.guests || [],
        },
        formValidation: {
            fullHinValidation: false,
            shortHinValidation: false,
            manufacturerValidation: false,
            boatModelValidation: false,
            deviceValidation: false,
            engineManufacturerValidation: false,
            engineModelValidation: false,
            dealerValidation: false,
            isFormDirty: false,
            isFormValid: false,
            nicknameValidation: false,
        },

        ownerName: '',
        engineManufacturer: row.engineManufacturer || '',
        boatManufacturers: [],
        engines: [],
        boatManufacturerOptions: [],
        permissionsCheck: permissionCheck,
        deviceOptions: [],
        boatModelOptions: [],
        dealerOptions: [],
        userOptions: [],
        userGuestOptions: [],
        engineOptions: [],
        fullHinCharacterCount: '',
        entryId: undefined,
        successfulSubmission: false,
        userQuery: '',
        dealerQuery: '',
        boatModelQuery: '',
        deviceQuery: '',
        userGuestQuery: '',
        guestInviteEmail: '',
        guestInviteSending: false,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        formValidation,
        boatManufacturers,
        engines,
        permissionsCheck,
        dealerName,
        ownerName,
        model,
        boatManufacturerName,
        dealerOptions,
        deviceOptions,
        boatModelOptions,
        userOptions,
        boatManufacturerOptions,
        engineOptions,
        fullHinCharacterCount,
        entryId,
        successfulSubmission,
        userQuery,
        dealerQuery,
        boatModelQuery,
        deviceQuery,
        userGuestQuery,
        userGuestOptions,
        guestInviteEmail,
        guestInviteSending,
    } = state

    const {
        fullHin,
        shortHin,
        nickname,
        engineModel,
        engineManufacturer,
        device,
        ownerEmail,
        productionStartDate,
        shipDate,
        deliveryDate,
        gpsEnabled,
        bluetoothEnabled,
        dealerVisibility,
        boatModel,
        dealer,
        updatedBy,
        manufacturer,
        geoFencing,
        engineSerial,
        tags,
        telemetryPreferences,
        owner,
        guests,
        demoBoat,
    } = formData

    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { activeManufacturer } = oupUser

    const telemetryPreferenceLabel = (preference: string) => {
        let label: string = ''
        switch (preference) {
            case 'geoFencing':
                label = 'Enable Geofencing'
                break
            case 'ownerGps':
                label = 'Share Boat GPS'
                break
            case 'guestGps':
                label = 'Share Boat GPS with Guests'
                break
            case 'dealerGps':
                label = 'Share Boat GPS with Dealer'
                break
            case 'manufacturerGps':
                label = 'Share Boat GPS with Manufacturer'
                break
            case 'ownerSignalNotifications':
                label = 'Owner Recieves Signal Notifications'
                break
            case 'guestSignalNotifications':
                label = 'Guests Recieve Signal Notifications'
                break

            case 'dealerSignalNotifications':
                label = 'Dealer Recieves Signal Notifications'
                break
            case 'manufacturerSignalNotifications':
                label = 'Manufacturer Recieves Signal Notifications'
                break
            default:
                label = 'ADD ME TO telemetryPreferenceLabel.'
        }
        return label
    }

    const {
        fullHinValidation,
        shortHinValidation,
        nicknameValidation,
        manufacturerValidation,
        boatModelValidation,
        deviceValidation,
        dealerValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    const handleChange = (e: any) => {
        const { name, value } = e.target

        if (name === 'manufacturer') {
            setState({
                formData: {
                    ...formData,
                    [name]: value ? value : '',
                    boatModel: '',
                },
            })
            return saveCache({
                formData: {
                    ...formData,
                    [name]: value ? value : '',
                    boatModel: '',
                },
            })
        } else if (name === 'owner') {
            const updatedFormData = {
                formData: {
                    ...formData,
                    owner: value,
                },
            }

            setState({
                formData: {
                    ...formData,
                    owner: value,
                },
                owner,
            })

            return saveCache(updatedFormData)
        } else if (name === 'fullHin') {
            const characterCount = value
            setState({
                fullHinCharacterCount: characterCount,
                formData: { ...formData, [name]: value },
            })
        } else if (name === 'boatModel') {
            setState({ formData: { ...formData, boatModel: value } })
        } else {
            setState({
                formData: { ...formData, [name]: value },
                [name]: value ? value : '',
            })
            return saveCache({
                formData: { ...formData, [name]: value },
                [name]: value ? value : '',
            })
        }
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (name === 'fullHin') {
            if (value && value.length === 14) {
                setState({
                    formValidation: {
                        ...formValidation,
                        [name + 'Validation']: false,
                        isFormDirty: true,
                    },

                    formData: {
                        ...formData,
                        shortHin: `${value.substring(5, 10)}`,
                    },
                })
            } else {
                setState({
                    formValidation: {
                        ...formValidation,
                        [name + 'Validation']: true,
                        isFormDirty: true,
                    },

                    formData: {
                        ...formData,
                        shortHin: '',
                    },
                })
            }
        } else {
            if (value !== '') {
                setState({
                    formValidation: {
                        ...formValidation,
                        [name + 'Validation']: false,
                        isFormDirty: true,
                    },
                })
            } else {
                setState({
                    formValidation: {
                        ...formValidation,
                        [name + 'Validation']: true,
                        isFormDirty: true,
                    },
                })
            }
        }
    }

    const handleTagSubmit = async (tagData, removedTags) => {
        try {
            if (successfulSubmission) {
                const tag = await addTag({ tagData, entryId })

                if (tag) {
                    const { data, status } = tag

                    if (data && status === 200) {
                        console.log('Tag updated successfully')
                    } else {
                        responseError(tag, row)
                    }
                }

                if (removedTags && removedTags.length > 1) {
                    const removeTags: any = await unlinkTag({ removedTags })

                    if (removeTags) {
                        const { data, status } = removeTags

                        if (data && status === 200) {
                            console.log('Tag removed successfully')
                        } else {
                            responseError(removeTags, row)
                        }
                    }
                }
                setState({ successfulSubmission: false })
                onClose(row.fullHin, row)
            }
        } catch (err) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleSubmit = async () => {
        try {
            const findManufacturer = boatManufacturers.find(
                ({ manufacturerName }) => manufacturerName === manufacturer
            )

            const provisionalBoatValidation =
                row?.provisionalBoat === true ? false : false

            const { ...boatFields } = formData

            const toGuestId = (guest: any) =>
                guest && typeof guest === 'object' ? guest._id ?? guest : guest

            const guestIds = (guests || []).map(toGuestId)
            const initialGuestIds = (row?.guests || []).map(toGuestId)

            const guestsChanged =
                guestIds.length !== initialGuestIds.length ||
                guestIds.some(
                    (id, i) => String(id) !== String(initialGuestIds[i])
                )

            const parsedFormData = {
                ...boatFields,
                manufacturer: findManufacturer?._id,
                device: device?._id,
                boatModel: boatModel?._id,
                dealer: dealer?._id,
                owner: owner?._id,
                guests: guestsChanged ? guestIds : undefined,
                provisionalBoat: provisionalBoatValidation,
            }

            let res: any

            res = await addBoat(parsedFormData)

            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `Boat: ${data.fullHin} was ${
                            row.id ? 'edited' : 'added'
                        } successfully`,
                        'success'
                    )

                    setState({ entryId: data?._id, successfulSubmission: true })
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    // Invite a guest by email. The backend (registration-add-guest) checks
    // whether the email already has an account: if so it is added straight to
    // the boat's guests; if not, a UserRegistration is staged and an email is
    // sent inviting them to create an account so they become a boat guest.
    const handleInviteGuest = async () => {
        const boatId = row?._id || row?.id || entryId
        const email = (guestInviteEmail || '').trim()

        if (!boatId) {
            setToastMessage(
                'Please save the boat before inviting guests.',
                'error'
            )
            return
        }
        if (!email) {
            setToastMessage('Please enter a guest email.', 'error')
            return
        }

        try {
            setState({ guestInviteSending: true })
            const res: any = await addGuestToRegistration({
                userEmail: email,
                boat: {
                    _id: boatId,
                    nickname,
                    fullHin,
                    manufacturer: row?.manufacturer?._id,
                },
            })

            if (res && res.status === 200) {
                setToastMessage(
                    res.data || `Guest invite sent to ${email}.`,
                    'success'
                )
                setState({ guestInviteEmail: '' })
            } else {
                setToastMessage(res?.data || 'Guest invite failed.', 'error')
            }
        } catch (err: any) {
            setToastMessage(`${err?.response?.data || err}`, 'error')
        } finally {
            setState({ guestInviteSending: false })
        }
    }

    // const getDeviceBoatData = async () => {
    //     let devices: any = []
    //     let deviceOptions: any = []
    //     let boatModels: any = []
    //     let boatModelOptions: any = []

    //     try {
    //         const getDeviceData = await getDevices(true)
    //         const getBoatModelData = await getBoatModels(true)

    //         if (getDeviceData && getDeviceData.length !== 0) {
    //             devices = getDeviceData.map((device) => device)

    //             deviceOptions = getDeviceData
    //                 .filter((device) => !device.boat)
    //                 .map((device) => device?.deviceSerial)
    //         }

    //         if (getBoatModelData && getBoatModelData.length !== 0) {
    //             boatModels = getBoatModelData

    //             boatModelOptions = getBoatModelData
    //                 .sort((a, b) => b.modelYear - a.modelYear)
    //                 .map((model) => `${model.modelYear} - ${model.model}`)
    //         }

    //         setState({
    //             devices,
    //             deviceOptions,
    //             boatModels,
    //             boatModelOptions,
    //         })
    //     } catch (err) {
    //         console.error(err, 'error')
    //         setToastMessage(`${err}`, 'error')
    //     }
    // }

    // const getData = async () => {

    //     let dealers: any = []
    //     let dealerOptions: any = []

    //     let boatManufacturers: any = []
    //     let boatManufacturerOptions: any = []

    //     try {

    //         const getDealerData = await getDealers(true)

    //         const getBoatManufacturerData = await getManufacturers(true)

    //         if (getDealerData && getDealerData.length !== 0) {
    //             dealers = getDealerData
    //             dealerOptions = dealers.map((dealer) => dealer?.dealerName)
    //         }

    //         if (
    //             getBoatManufacturerData &&
    //             getBoatManufacturerData.length !== 0
    //         ) {
    //             boatManufacturers = getBoatManufacturerData.filter(
    //                 (manufacturer) => manufacturer.manufacturerType === 'boat'
    //             )
    //             boatManufacturerOptions = boatManufacturers.map(
    //                 ({ manufacturerName }) => manufacturerName
    //             )
    //         }

    //         setState({

    //             dealers,
    //             dealerOptions,
    //             boatManufacturers,
    //             boatManufacturerOptions,

    //         })
    //     } catch (err) {
    //         console.error(err, 'error')
    //         setToastMessage(`${err}`, 'error')
    //     }
    // }

    const getData = async () => {
        try {
            const [manufacturersRes] = await Promise.all([
                getManufacturers(true),
            ])

            const boatManufacturers = (manufacturersRes || []).filter(
                (m) => m.manufacturerType === 'boat'
            )
            const boatManufacturerOptions = boatManufacturers.map(
                (m) => m.manufacturerName
            )

            setState({
                boatManufacturers,
                boatManufacturerOptions,
            })
        } catch (err) {
            console.error('Dropdown data load failed:', err)
            setToastMessage('Failed to load dropdown options', 'error')
        }
    }

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string, queryKey: string) => {
                setState({ [queryKey]: value })
            }, 600),
        []
    )

    useEffect(() => {
        const getSearchedUsers = async () => {
            try {
                const searchedUsers = await getUserQuery({
                    active: true,
                    search: userQuery || userGuestQuery,
                    limit: 20,
                })

                if (!searchedUsers || searchedUsers.length === 0) {
                    setState({ userOptions: [] })
                    return
                }

                const searchResults = searchedUsers.map((user: any) => ({
                    _id: user._id,
                    userEmail: user.userEmail,
                    userFirstName: user.userFirstName,
                    userLastName: user.userLastName,
                    label: `${user?.userEmail || ''} - ${user?.userLastName}, ${
                        user?.userFirstName
                    }`,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        userOptions: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedUsers()
    }, [userQuery])

    useEffect(() => {
        const getSearchedUsers = async () => {
            try {
                const searchedUsers = await getUserQuery({
                    active: true,
                    search: userGuestQuery,
                    limit: 20,
                })

                if (!searchedUsers || searchedUsers.length === 0) {
                    setState({ userGuestOptions: [] })
                    return
                }

                const searchResults = searchedUsers.map((user: any) => ({
                    _id: user._id,
                    userEmail: user.userEmail,
                    userFirstName: user.userFirstName,
                    userLastName: user.userLastName,
                    label: `${user?.userEmail || ''} - ${user?.userLastName}, ${
                        user?.userFirstName
                    }`,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        userGuestOptions: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedUsers()
    }, [userGuestQuery])

    useEffect(() => {
        const getSearchedBoatModels = async () => {
            try {
                const searchedModels = await getBoatModelQuery({
                    active: true,
                    search: boatModelQuery,
                    limit: 50,
                })

                if (!searchedModels || searchedModels.length === 0) {
                    setState({ boatModelOptions: [] })
                    return
                }

                const searchResults = searchedModels.map((item: any) => ({
                    _id: item?._id,
                    label: `${item?.modelYear} - ${item?.model}`,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        boatModelOptions: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedBoatModels()
    }, [boatModelQuery])

    useEffect(() => {
        const getSearchedDealers = async () => {
            try {
                const searchedDealers = await getDealerQuery({
                    active: true,
                    search: dealerQuery,
                    limit: 20,
                })

                if (!searchedDealers || searchedDealers.length === 0) {
                    setState({ dealerOptions: [] })
                    return
                }

                const searchResults = searchedDealers.map((item: any) => ({
                    _id: item?._id,
                    dealerName: item?.dealerName,
                    label: item?.dealerName,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        dealerOptions: searchResults,
                        dealers: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedDealers()
    }, [dealerQuery])

    useEffect(() => {
        const getSearchedDevices = async () => {
            try {
                const searchedDevices = await getDeviceQuery({
                    active: true,
                    search: deviceQuery,
                    limit: 20,
                    manufacturer: activeManufacturer?._id,
                })

                if (!searchedDevices || searchedDevices.length === 0) {
                    setState({ deviceOptions: [] })
                    return
                }

                const searchResults = searchedDevices.map((item: any) => ({
                    _id: item?._id,
                    deviceSerial: item?.deviceSerial,
                    label: item?.deviceSerial,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        deviceOptions: searchResults,
                        devices: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedDevices()
    }, [deviceQuery])

    useEffect(() => {
        getData()
        saveCache(row)
    }, [])

    // const filterModelsByManufacturerId = (manufacturer) => {
    //     if (manufacturer) {
    //         const findManufacturer = boatManufacturers.find(
    //             ({ manufacturerName }) => manufacturerName === manufacturer
    //         )

    //         const filteredModels = boatModels
    //             .filter(
    //                 (model) => model?.manufacturer._id === findManufacturer?._id
    //             )
    //             .sort((a, b) => b.modelYear - a.modelYear)
    //             .map((model) => `${model.modelYear} - ${model.model}`)

    //         return filteredModels
    //     } else {
    //         return []
    //     }
    // }

    // useEffect(() => {
    //     const findBoatModels = filterModelsByManufacturerId(manufacturer)

    //     if (findBoatModels) {
    //         setState({ boatModelOptions: findBoatModels })
    //     }
    // }, [manufacturer])

    useEffect(() => {
        if (
            isFormDirty &&
            !fullHinValidation &&
            !shortHinValidation &&
            !manufacturerValidation &&
            !boatModelValidation &&
            !nicknameValidation &&
            !deviceValidation &&
            !dealerValidation &&
            fullHin &&
            shortHin &&
            manufacturer &&
            boatModel &&
            nickname &&
            device &&
            dealer
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        fullHinValidation,
        shortHinValidation,
        manufacturerValidation,
        boatModelValidation,
        nicknameValidation,
        deviceValidation,
        dealerValidation,
        fullHin,
        shortHin,
        manufacturer,
        boatModel,
        nickname,
        device,
        dealer,
    ])

    const handleReset = () => {
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
            fullHinCharacterCount: defaultState.fullHinCharacterCount,
        })
    }

    const handleDateReset = () => {
        setState({
            ...state,
            formData: {
                ...formData,
                shipDate: defaultState.formData.shipDate,
                deliveryDate: defaultState.formData.deliveryDate,
                productionStartDate: defaultState.formData.productionStartDate,
            },
        })
    }

    return (
        <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid item xs={12}>
                <UniversalTagToolbar
                    row={row}
                    pageType="boats"
                    entryId={entryId}
                    addTag={handleTagSubmit}
                    setFormDirty={() =>
                        setState({
                            formValidation: {
                                ...formValidation,
                                isFormValid: true,
                            },
                        })
                    }
                />
            </Grid>
            {
                /// Left side of Grid
            }
            <Grid item xs={12} md={9}>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <FormSection title="Boat Info">
                            <Grid item md={6} xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required={true}
                                        fullWidth
                                        name="fullHin"
                                        label="HIN"
                                        disabled={
                                            permissionsCheck === true
                                                ? true
                                                : !!row.fullHin
                                        }
                                        value={fullHin}
                                        onChange={handleChange}
                                        onBlur={validateOnBlur}
                                        error={
                                            fullHinValidation &&
                                            fullHin.length !== 14
                                        }
                                        helperText={
                                            fullHinValidation &&
                                            fullHin.length !== 14
                                                ? 'Full HIN must be 14 characters long.'
                                                : ''
                                        }
                                        inputProps={{ maxLength: 14 }}
                                    />
                                    <Box>
                                        <Typography
                                            sx={{
                                                marginTop: 0.5,
                                                display: fullHinCharacterCount
                                                    ? ''
                                                    : 'none',
                                            }}
                                            fontSize={'small'}
                                            color={
                                                fullHinCharacterCount.length ===
                                                14
                                                    ? 'primary'
                                                    : '#AEAEAE'
                                            }
                                        >
                                            {`${
                                                fullHinCharacterCount.length
                                            } / ${14} Characters`}
                                        </Typography>
                                    </Box>
                                </FormControl>
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    name="shortHin"
                                    label="Short Hin"
                                    onBlur={validateOnBlur}
                                    disabled
                                    value={shortHin}
                                    error={shortHinValidation && !shortHin}
                                    helperText={
                                        shortHinValidation && !shortHin
                                            ? 'Please enter a enter a full Hin to populate the short Hin field.'
                                            : ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    required={true}
                                    fullWidth
                                    onChange={handleChange}
                                    onBlur={validateOnBlur}
                                    name="nickname"
                                    label="Boat Nickname"
                                    value={nickname}
                                    disabled={permissionsCheck}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Boat Manufacturer"
                                    name="manufacturer"
                                    value={manufacturer}
                                    disabled={true}
                                />
                                {/* <UniversalAutocomplete
                                    options={boatManufacturerOptions}
                                    value={manufacturer}
                                    handleChange={handleChange}
                                    required={true}
                                    onBlur={validateOnBlur}
                                    label="Boat Manufacturer"
                                    name="manufacturer"
                                    error={
                                        manufacturerValidation && !manufacturer
                                    }
                                    helperText={
                                        manufacturer && !manufacturer
                                            ? 'Please select a Enter A Manufacturer Name.'
                                            : ''
                                    }
                                    disabled={true}
                                /> */}
                            </Grid>
                            <Grid item xs={6}>
                                <UniversalAutocomplete
                                    onBlur={validateOnBlur}
                                    options={boatModelOptions || []}
                                    handleChange={handleChange}
                                    value={boatModel ? boatModel?.label : ''}
                                    fullWidth
                                    required
                                    label="Boat Model"
                                    name="boatModel"
                                    error={boatModelValidation && !boatModel}
                                    helperText={
                                        boatModelValidation && !boatModel
                                            ? 'Please enter a select a Boat Model.'
                                            : ''
                                    }
                                    disabled={permissionsCheck}
                                    onInputChange={(e, newValue) => {
                                        debouncedSearch(
                                            newValue,
                                            'boatModelQuery'
                                        )
                                    }}
                                    placeholder="Enter a model year or model name to search..."
                                    noOptionsText={
                                        boatModel
                                            ? 'No additional boat models found - please try a different query'
                                            : 'No boat models found - please try a different query'
                                    }
                                />
                            </Grid>
                            <Grid item xs={9}>
                                <UniversalAutocomplete
                                    options={deviceOptions}
                                    value={device}
                                    handleChange={handleChange}
                                    required={true}
                                    onBlur={validateOnBlur}
                                    label="Device Serial"
                                    name="device"
                                    error={deviceValidation && !device}
                                    helperText={
                                        deviceValidation && !device
                                            ? 'Please select a Device Serial.'
                                            : ''
                                    }
                                    disabled={permissionsCheck}
                                    onInputChange={(e, newValue) => {
                                        debouncedSearch(newValue, 'deviceQuery')
                                    }}
                                    placeholder="Enter a device serial to search..."
                                    noOptionsText={
                                        'No device serial found - please try a different query'
                                    }
                                />
                            </Grid>
                        </FormSection>
                    </Grid>
                </Grid>

                <FormSection title="Engine Info">
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            id="engineManufacturer"
                            name="engineManufacturer"
                            label="Engine Manufacturer"
                            value={engineManufacturer}
                            onChange={handleChange}
                            onBlur={validateOnBlur}
                            disabled={permissionsCheck}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="engineModel"
                            onChange={handleChange}
                            onBlur={validateOnBlur}
                            value={engineModel}
                            name="engineModel"
                            label="Engine Model"
                            disabled={permissionsCheck}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            id="engineSerial"
                            onChange={handleChange}
                            onBlur={validateOnBlur}
                            value={engineSerial}
                            name="engineSerial"
                            label="Engine Serial"
                            disabled={permissionsCheck}
                        />
                    </Grid>
                </FormSection>

                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <FormSection title="Dealer Info">
                                <Grid item xs={12} sx={{ marginBottom: 2 }}>
                                    <UniversalAutocomplete
                                        onBlur={validateOnBlur}
                                        label="Dealer Name"
                                        options={dealerOptions}
                                        handleChange={handleChange}
                                        value={dealer}
                                        name="dealer"
                                        required={true}
                                        disabled={permissionsCheck}
                                        error={dealerValidation && !dealer}
                                        onInputChange={(e, newValue) => {
                                            debouncedSearch(
                                                newValue,
                                                'dealerQuery'
                                            )
                                        }}
                                        helperText={
                                            dealerValidation && !dealer
                                                ? 'Please Provide a Dealer Name.'
                                                : ''
                                        }
                                        fullWidth
                                        placeholder="Enter a dealer name to search..."
                                        noOptionsText={
                                            'No matching dealers found - please try a different query'
                                        }
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={8} sx={{ marginBottom: 2 }}>
                            <FormSection title="Owner Info">
                                <Grid item xs={7}>
                                    <UniversalAutocomplete
                                        onBlur={validateOnBlur}
                                        options={userOptions || []}
                                        handleChange={handleChange}
                                        name="owner"
                                        label="Owner Email"
                                        value={owner ? owner?.userEmail : ''}
                                        fullWidth
                                        disabled={permissionsCheck}
                                        onInputChange={(e, newValue) => {
                                            debouncedSearch(
                                                newValue,
                                                'userQuery'
                                            )
                                        }}
                                        placeholder="Enter an email to search.."
                                        noOptionsText="No users found - please try a different query"
                                    />
                                </Grid>
                                <Grid item xs={5} sx={{ marginBottom: 2 }}>
                                    <TextField
                                        id="owner"
                                        fullWidth
                                        name="owner"
                                        onBlur={validateOnBlur}
                                        label="Owner"
                                        value={
                                            owner
                                                ? `${owner?.userFirstName} ${owner?.userLastName}`
                                                : ''
                                        }
                                        onChange={handleChange}
                                        disabled
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} sx={{ marginTop: -4 }}>
                            <FormSection
                                title="Boat Guests"
                                titleAlignment="center"
                            >
                                <Grid item md={12} xs={12}>
                                    <Autocomplete
                                        multiple
                                        id="guests"
                                        value={guests}
                                        onBlur={() =>
                                            setState({
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                        }
                                        onChange={(event, newValue) => {
                                            const data = {
                                                target: {
                                                    name: 'guests',
                                                    value: newValue,
                                                },
                                            }

                                            handleChange(data)
                                        }}
                                        options={userGuestOptions.filter(
                                            (user) =>
                                                user?._id !== owner?._id &&
                                                guests.findIndex(
                                                    (guest) =>
                                                        guest?._id === user?._id
                                                ) === -1
                                        )}
                                        onInputChange={(e, newValue) => {
                                            debouncedSearch(
                                                newValue,
                                                'userGuestQuery'
                                            )
                                        }}
                                        getOptionLabel={(option) =>
                                            option?.userEmail
                                        }
                                        noOptionsText="No guests found - please try a different query"
                                        renderTags={(tagValue, getTagProps) =>
                                            tagValue.map(
                                                (option: any, index) => (
                                                    <Chip
                                                        {...getTagProps({
                                                            index,
                                                        })}
                                                        label={
                                                            option?.userEmail
                                                        }
                                                        variant={'filled'}
                                                        onDelete={(e) => {
                                                            getTagProps({
                                                                index,
                                                            }).onDelete(e)
                                                            setState({
                                                                formValidation:
                                                                    {
                                                                        ...formValidation,
                                                                        isFormDirty:
                                                                            true,
                                                                    },
                                                            })
                                                        }}
                                                        disabled={
                                                            guests.indexOf(
                                                                index
                                                            ) !== -1
                                                        }
                                                        onClick={() =>
                                                            setState({
                                                                formData: {
                                                                    ...formData,
                                                                    guests:
                                                                        guests &&
                                                                        guests._id ===
                                                                            option?._id
                                                                            ? {}
                                                                            : option,
                                                                },
                                                            })
                                                        }
                                                    />
                                                )
                                            )
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                name="guests"
                                                label="Add Guests by Email"
                                                placeholder="Enter an email to search..."
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={12}
                                    xs={12}
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                        marginTop: 2,
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        name="guestInviteEmail"
                                        label="Invite a new guest by email"
                                        placeholder="Enter an email to invite..."
                                        value={guestInviteEmail}
                                        onChange={(e) =>
                                            setState({
                                                guestInviteEmail:
                                                    e.target.value,
                                            })
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                handleInviteGuest()
                                            }
                                        }}
                                        helperText="If the email has no account, they'll be emailed an invite to create one and be added as a guest."
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleInviteGuest}
                                        disabled={
                                            guestInviteSending ||
                                            !guestInviteEmail
                                        }
                                        sx={{
                                            marginTop: 1,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {guestInviteSending
                                            ? 'Sending...'
                                            : 'Send Invite'}
                                    </Button>
                                </Grid>
                            </FormSection>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
                <Grid item xs={12} sx={{ marginBottom: 2 }}>
                    <FormSection title="Sales Info">
                        <Grid container spacing={2} sx={{ marginBottom: 0.5 }}>
                            <Grid item xs={12}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        label="Production Start Date"
                                        views={['year', 'month', 'day']}
                                        sx={{ width: '100%' }}
                                        value={productionStartDate}
                                        onAccept={($d) => {
                                            basicValidator(
                                                {
                                                    target: {
                                                        name: 'productionStartDate',
                                                        value: productionStartDate,
                                                    },
                                                },
                                                formValidation,
                                                setState
                                            )
                                            setState({
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                            const event = {
                                                target: {
                                                    value: $d,
                                                    name: 'productionStartDate',
                                                },
                                            }

                                            handleChange(event)
                                        }}
                                        disabled={permissionsCheck}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        label="Ship Date"
                                        views={['year', 'month', 'day']}
                                        sx={{ width: '100%' }}
                                        value={shipDate}
                                        onAccept={($d) => {
                                            basicValidator(
                                                {
                                                    target: {
                                                        name: 'shipDate',
                                                        value: shipDate,
                                                    },
                                                },
                                                formValidation,
                                                setState
                                            )
                                            setState({
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                            const event = {
                                                target: {
                                                    value: $d,
                                                    name: 'shipDate',
                                                },
                                            }

                                            handleChange(event)
                                        }}
                                        disabled={permissionsCheck}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                >
                                    <DatePicker
                                        label="Retail Delivery Date"
                                        views={['year', 'month', 'day']}
                                        sx={{ width: '100%' }}
                                        value={deliveryDate}
                                        onAccept={($d) => {
                                            basicValidator(
                                                {
                                                    target: {
                                                        name: 'deliveryDate',
                                                        value: deliveryDate,
                                                    },
                                                },
                                                formValidation,
                                                setState
                                            )
                                            setState({
                                                formValidation: {
                                                    ...formValidation,
                                                    isFormDirty: true,
                                                },
                                            })
                                            const event = {
                                                target: {
                                                    value: $d,
                                                    name: 'deliveryDate',
                                                },
                                            }

                                            handleChange(event)
                                        }}
                                        disabled={permissionsCheck}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleDateReset}
                                    disabled={permissionsCheck}
                                    sx={{
                                        width: '100%',
                                        height: 55,
                                    }}
                                >
                                    Reset Sales Info
                                </Button>
                            </Grid>
                        </Grid>
                    </FormSection>
                </Grid>

                <Grid container sx={{ marginBottom: 1 }}>
                    <UniversalAccordion accordionTitle="Settings">
                        <Grid container>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            onChange={() =>
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        bluetoothEnabled:
                                                            !bluetoothEnabled,
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }
                                            inputProps={{
                                                'aria-label': 'controlled',
                                            }}
                                            id="switch.bluetoothEnabled"
                                            name="switch.bluetoothEnabled"
                                            checked={bluetoothEnabled}
                                            key="switch.bluetoothEnabled"
                                        />
                                    }
                                    label="Enable Bluetooth Boat Controls."
                                    key="switch.bluetoothEnabled"
                                    disabled={permissionsCheck}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            onChange={() =>
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        dealerVisibility:
                                                            !dealerVisibility,
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }
                                            inputProps={{
                                                'aria-label': 'controlled',
                                            }}
                                            id="switch.dealerVisibility"
                                            name="switch.dealerVisibilty"
                                            checked={dealerVisibility}
                                            key="switch.dealerVisibilty"
                                        />
                                    }
                                    label="Boat Visible to Dealer"
                                    key="switch.dealerVisibilty"
                                    disabled={permissionsCheck}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            onChange={() =>
                                                setState({
                                                    formData: {
                                                        ...formData,
                                                        demoBoat: !demoBoat,
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                })
                                            }
                                            inputProps={{
                                                'aria-label': 'controlled',
                                            }}
                                            id="switch.demoBoat"
                                            name="switch.demoBoat"
                                            checked={demoBoat}
                                            key="switch.demoBoat"
                                        />
                                    }
                                    label="Demo Boat"
                                    key="switch.demoBoat"
                                    disabled={permissionsCheck}
                                />
                            </Grid>
                        </Grid>
                    </UniversalAccordion>
                    <UniversalAccordion
                        accordionTitle="Telemetry Preferences"
                        accordionTitleDescription="Enable/Disable Telemetry Preferences"
                    >
                        {telemetryPreferences &&
                            Object.keys(telemetryPreferences)?.length !== 0 &&
                            Object.keys(telemetryPreferences).map(
                                (preference) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                onChange={() =>
                                                    setState({
                                                        formData: {
                                                            ...formData,
                                                            telemetryPreferences:
                                                                {
                                                                    ...telemetryPreferences,
                                                                    [preference]:
                                                                        !telemetryPreferences[
                                                                            preference
                                                                        ],
                                                                },
                                                        },
                                                        formValidation: {
                                                            ...formValidation,
                                                            isFormDirty: true,
                                                        },
                                                    })
                                                }
                                                inputProps={{
                                                    'aria-label': 'controlled',
                                                }}
                                                id={`switch.${preference}`}
                                                name={`switch.${preference}`}
                                                checked={
                                                    telemetryPreferences[
                                                        preference
                                                    ]
                                                }
                                                key={`switch.${preference}`}
                                            />
                                        }
                                        label={telemetryPreferenceLabel(
                                            preference
                                        )}
                                        sx={
                                            telemetryPreferences[preference]
                                                ? { color: 'primary' }
                                                : { color: 'grey' }
                                        }
                                        key={`switch.${preference}`}
                                        disabled={permissionsCheck}
                                    />
                                )
                            )}
                    </UniversalAccordion>
                </Grid>
            </Grid>

            <Grid item xs={12} sx={{ marginTop: -4 }}>
                <SaveButton
                    title="Required Fields Are Marked With An *"
                    manufacturer={row.manufacturerName}
                    titleAlignment="center"
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    disabledSave={!isFormValid || permissionCheck}
                    disabledReset={!isFormDirty || permissionCheck}
                />
            </Grid>
        </Grid>
    )
}

export default BoatConfigurationForm
